/*
    dom.jsx
    DOM operations
*/

import {isIE} from './browser';
import ResizeObserver from 'resize-observer-polyfill';

export const scrolling = (() => {
    let depth = 0;
    let setHidden = false;
    let originalOverflow = null;
    return {
        disable() {
            depth += 1;
            if (!setHidden) {
                originalOverflow = document.body.style.overflow;
                document.body.style.overflow = "hidden";
                setHidden = true;
            }
        },
        enable() {
            depth = Math.max(0, depth - 1);
            if (depth <= 0 && setHidden) {
                document.body.style.overflow = originalOverflow;
                originalOverflow = null;
                setHidden = false;
            }
        }
    };
})();

export const onImageLoad = (img, callback) => {
    if (img.complete) {
        callback();
        return;
    }

    if (img.decode && typeof img.decode === 'function') {
        // This is the official way to wait for a download
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/decode
        img.decode().then(callback).catch(callback);
    } else {
        callback();
    }
};

export const replaceNode = (from, to) => {
    // Compatibility with IE
    if (from.replaceWith && typeof from.replaceWith === 'function') {
        from.replaceWith(to);
    } else {
        from.parentNode.replaceChild(to, from);
    }
};

// Scrolls to a Y position
export const scroll = ({to, by, behavior}) =>
    window[to ? 'scrollTo' : 'scrollBy'].apply(window, isIE() ? [0, to ? to : by] : [{top: to ? to : by, behavior}]);

export const observeMutations = (root, cb) => {
    let obs = new MutationObserver(mutations => {
        for (let mut of mutations) {
            for (let n of mut.addedNodes) {
                if (isElement(n)) cb(n, true);
            }
            for (let n of mut.removedNodes) {
                if (isElement(n)) cb(n, false);
            }
        }
    });
    obs.observe(root, {subtree: true, childList: true});
    return () => obs.disconnect();
};

export const observeIntersection = (cb, args={}) => {
    let {root, rootMargin, threshold} = args;
    if (!root) root = null;
    if (!rootMargin) rootMargin = "0px";
    if (!threshold) threshold = [0];
    let ref = {};

    const isIntersecting = e => e.isIntersecting;
    const toTarget = e => e.target;

    const onEntries = entries => {
        let nodes = entries.filter(isIntersecting).map(toTarget).filter(isElement);
        return cb(nodes, observe, unobserve);
    };

    const observe = el => {
        ref.current.observe(el);
        onEntries(ref.current.takeRecords());
    };
    const unobserve = el => ref.current.unobserve(el);

    ref.current = new IntersectionObserver(onEntries, {root, rootMargin, threshold});
    return {cancel: () => {
        if (ref.current) {
            onEntries(ref.current.takeRecords());
            ref.current.disconnect();
        }
    }, observe, unobserve};
};

export const observeResize = (el, cb) => {
    let r = new ResizeObserver(entries => {
        cb(entries[0].contentRect);
    });
    r.observe(el);
    return () => r.disconnect();
};

export function onVisibleElement(query, callback, batched = false) {
    const {cancel:cancelInt, observe:observeInt, unobserve:unobserveInt} = observeIntersection(nodes => {
        if (batched) {
            callback(nodes);
            nodes.forEach(unobserveInt);
        } else {
            nodes.forEach(node => {
                callback(node);
                unobserveInt(node);
            });
        }
    });

    const cancelMut = observeMutations(document.body, (node, added) => {
        Array.from((node.parentNode ? node.parentNode : node).querySelectorAll(query))
             .forEach(added ? observeInt : unobserveInt);
    });

    Array.from(document.body.querySelectorAll(query)).forEach(observeInt);

    return () => {
        cancelInt();
        cancelMut();
    };
}

export function whenVisible(element, callback) {
    let threshold = [];
    for (let i = 0; i <= 1; i += 0.1) threshold.push(i);

    const {cancel, observe, unobserve} = observeIntersection(nodes => {
        for (let n of nodes) {
            if (n === element) {
                unobserve(n);
                cancel();
                callback();
                break;
            }
        }
    }, {threshold});
    observe(element);
    return cancel;
}

// addEventListener that returns an unregister function.
// MUST BE A function() {} for `arguments`
export function listen() {
    const [target, ...args] = [...arguments];
    target.addEventListener(...args);
    return () => target.removeEventListener(...args);
}

// returns the first ancestor of el passing pred
export const ancestorMatching = (el, pred) => {
    let element = el;
    while (element) {
       if (element === document) break;

       if (pred(element)) {
           return element;
       }
       element = element.parentNode;
    }
    return null;
};

export const insertAfter = (newNode, referenceNode) => {
    if (referenceNode.nextSibling) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    } else {
        referenceNode.parentNode.appendChild(newNode);
    }
};

export const removeNode = node => {
    if (!node || !node.parentNode) return;
    node.parentNode.removeChild(node);
};

export const splitURL = (inputUrl = window.location.href) => {
    const url = new URL(inputUrl, window.location.origin);
    const hash = url.hash ? url.hash.slice(1) : null;
    const query = url.searchParams.toString();
    let rootRelative = url.pathname + (query ? '?' + query : '');

    if (rootRelative.startsWith('//localhost')) {
        rootRelative = rootRelative.replace('//localhost', '');
    }

    return {rootRelative, hash, origin: url.origin};
};

export const isElement = v => v.nodeType === 1;

export const detachChildren = (node, remove=true) => {
    let frag = document.createDocumentFragment();
    if (node && node.children) {
        for (let n of Array.from(node.children)) frag.appendChild(n);
        // The fastest way to clear children in one shot,
        // without fucking up jQuery or event handlers.
        if (remove) node.innerHTML = '';
    }
    return frag;
};

export const matchesSelector = (element, selector) => {
    const EP = Element.prototype;
    const fn = EP.matches || EP.webkitMatchesSelector || EP.mozMatchesSelector || EP.msMatchesSelector;

    return fn.call(element, selector);
};

export const closestMatching = (element, selector) => {
    if (element.closest) return element.closest(selector);

    do {
      if (element.matches(selector)) return element;
      element = element.parentElement || element.parentNode;
    } while (element !== null && element.nodeType === 1);
    return null;
};

export default {
    replaceNode,
    scroll,
    observeMutations,
    observeIntersection,
    observeResize,
    detachChildren,
    isElement,
    whenVisible,
    onVisibleElement,
    listen,
    matchesSelector,
    splitURL,
    ancestorMatching
};

